/**
 * SEO helpers for shop pages. They work on the shop object returned by
 * GET /api/main/shop/:id and mirror the server-side versions in
 * Sthanickbackend/src/controller/seoController.js - keep the two in sync.
 */
export const SITE_URL = "https://www.sthanick.com";

const byCategoryId = (a, b) => Number(a.category_id) - Number(b.category_id);

export const primaryCategory = (shop) => {
  const regular = [...(shop?.categories || [])].sort(byCategoryId)[0];
  const listing = [...(shop?.listCategories || [])].sort(byCategoryId)[0];
  return shop?.reg_list_type === "listing" ? listing || regular : regular;
};

/** "pocharam" -> "Pocharam"; leaves already-cased text like "SR Nagar" alone */
const niceCase = (value) => {
  const text = String(value || "").trim();
  return text && text === text.toLowerCase()
    ? text.replace(/(^|[\s-])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase())
    : text;
};

/** Street, city, state without repeats ("pocharam, pocharam" -> "Pocharam") */
const placeOf = (shop) => {
  const seen = new Set();
  return [shop.street, shop.city, shop.state]
    .map(niceCase)
    .filter((part) => {
      const key = part.toLowerCase();
      if (!part || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(", ");
};

/** "09:00 AM" / "9:00" / "21:30" -> "09:00" / "21:30" (schema.org wants 24h HH:MM) */
const to24h = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const meridiem = match[3]?.toLowerCase().replace(/\./g, "");
  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;
  if (hours > 23) return null;

  return `${String(hours).padStart(2, "0")}:${match[2] || "00"}`;
};

export const shopTitle = (shop) => {
  const category = primaryCategory(shop)?.name;
  return `${shop.shop_name}${shop.city ? `, ${niceCase(shop.city)}` : ""}${category ? ` - ${category}` : ""} | Sthanick`;
};

/** Plain-English summary, used for the meta description and the "About" text */
export const describeShop = (shop) => {
  const category = primaryCategory(shop)?.name;
  const place = placeOf(shop);
  const parts = [
    `${shop.shop_name}${category ? ` (${category})` : ""} is a local shop${place ? ` in ${place}` : ""}${shop.pincode ? ` ${shop.pincode}` : ""}.`,
  ];
  if (shop.open_time && shop.close_time) {
    parts.push(`Open ${shop.open_time} - ${shop.close_time}.`);
  }
  if (shop.rating) {
    parts.push(
      `Rated ${shop.rating.value}/5 by ${shop.rating.count} customer${shop.rating.count === 1 ? "" : "s"}.`,
    );
  }
  parts.push("View products, phone number and directions on Sthanick.");
  return parts.join(" ");
};

export const buildShopJsonLd = (shop, url) => {
  const opens = to24h(shop.open_time);
  const closes = to24h(shop.close_time);
  const latitude = Number.parseFloat(shop.latitude);
  const longitude = Number.parseFloat(shop.longitude);

  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${url}#shop`,
    name: shop.shop_name,
    url,
    description: describeShop(shop),
    image: shop.store_image || undefined,
    telephone: shop.phone_number ? `+91${shop.phone_number}` : undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: niceCase(shop.street) || undefined,
      addressLocality: niceCase(shop.city) || undefined,
      addressRegion: niceCase(shop.state) || undefined,
      postalCode: shop.pincode || undefined,
      addressCountry: "IN",
    },
    geo:
      Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { "@type": "GeoCoordinates", latitude, longitude }
        : undefined,
    openingHoursSpecification:
      opens && closes
        ? {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ],
            opens,
            closes,
          }
        : undefined,
    // Only real, admin-approved customer ratings - never a placeholder
    aggregateRating: shop.rating
      ? {
          "@type": "AggregateRating",
          ratingValue: shop.rating.value,
          reviewCount: shop.rating.count,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined,
  };

  // Drop empty values so Google doesn't see blank fields
  return JSON.parse(JSON.stringify(data));
};
